import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import Map from './Map';
import Timeline from './Timeline';
import Button from './Button';
import { fetchDatasetLocations } from '../services/datasetsService';
import { useError } from '../contexts/ErrorContext';
import './DatasetCropView.css';

const DatasetCropView = ({ isOpen, onClose, dataset, onSave }) => {
  const { addError } = useError();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);
  const [isSelectingStart, setIsSelectingStart] = useState(true);

  const loadDatasetLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDatasetLocations(dataset.id, addError);
      setLocations(data);
    } catch (err) {
      const errorMsg = err.message || 'Failed to load dataset';
      setError(errorMsg);
      addError(errorMsg, err.stack, 'Dataset Crop View');
      console.error('Failed to load dataset locations:', err);
    } finally {
      setLoading(false);
    }
  }, [dataset, addError]);

  useEffect(() => {
    if (isOpen && dataset) {
      loadDatasetLocations();
    }
  }, [isOpen, dataset, loadDatasetLocations]);

  useEffect(() => {
    if (locations.length > 0) {
      const minTime = locations[0].timestamp;
      const maxTime = locations[locations.length - 1].timestamp;
      setStartTime(minTime);
      setEndTime(maxTime);
      setCurrentTime(minTime);
    }
  }, [locations]);

  const handleTimeChange = (time) => {
    setCurrentTime(time);
    // Don't auto-update start/end while dragging - user must click Set Start/End
  };

  const handleSetStart = () => {
    if (currentTime !== null) {
      setStartTime(currentTime);
      setIsSelectingStart(true);
    }
  };

  const handleSetEnd = () => {
    if (currentTime !== null) {
      setEndTime(currentTime);
      setIsSelectingStart(false);
    }
  };

  const handleSave = () => {
    if (startTime && endTime && startTime < endTime) {
      onSave({
        dataset_id: dataset.id,
        start_time: startTime,
        end_time: endTime
      });
    } else {
      alert('Please select a valid time range (start time must be before end time)');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--:--';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString();
  };

  if (!isOpen || !dataset) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Crop Dataset: ${dataset.name}`} className="large-modal">
      <div className="dataset-crop-view">
        {loading && <div className="crop-loading">Loading dataset...</div>}
        {error && <div className="crop-error">Error: {error}</div>}
        {!loading && !error && locations.length > 0 && (
          <>
            <div className="crop-map-container">
              <Map
                locations={locations}
                currentTime={currentTime}
                height="700px"
                centerLatitude={locations[0]?.lat || 39.6061}
                centerLongitude={locations[0]?.lon || -106.3550}
              />
            </div>
            <div className="crop-timeline-container">
              <Timeline
                locations={locations}
                currentTime={currentTime}
                onTimeChange={handleTimeChange}
                isPlaying={false}
                onPlayPause={() => {}}
                disabled={false}
                sidebarOpen={false}
                sidebarWidth={0}
                hidePlayButton={true}
              />
            </div>
            <div className="crop-controls">
              <div className="crop-instructions">
                <p>Drag the timeline to select a time, then click "Set Start" or "Set End" to mark that position.</p>
              </div>
              <div className="crop-time-selection">
                <div className="time-selector">
                  <Button
                    variant={isSelectingStart ? "primary" : "outline"}
                    size="small"
                    onClick={handleSetStart}
                  >
                    Set Start
                  </Button>
                  <span className="time-display">
                    Start: {formatTime(startTime)}
                  </span>
                </div>
                <div className="time-selector">
                  <Button
                    variant={!isSelectingStart ? "primary" : "outline"}
                    size="small"
                    onClick={handleSetEnd}
                  >
                    Set End
                  </Button>
                  <span className="time-display">
                    End: {formatTime(endTime)}
                  </span>
                </div>
              </div>
              <div className="crop-actions">
                <Button
                  variant="outline"
                  size="medium"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handleSave}
                  disabled={!startTime || !endTime || startTime >= endTime}
                >
                  Add
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DatasetCropView;
